import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2 as CheckCircle, CloudUpload, DatabaseBackup as Database, FileText, Play, RefreshCw, ShieldAlert } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { useSondage } from '../../lib/sondage';
import { DEFAVORABLE, FAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import {
  ETATS_TACHE_LIBELLE,
  enregistrerIdentifiantsOAuth,
  exporterJournal,
  exporterMiroir,
  fetchExportsMiroir,
  fetchJournalSync,
  fetchManifeste,
  fetchSauvegardes,
  fetchSupervision,
  obtenirUrlConsentement,
  recetteDrive,
  relancerEchecs,
  revoquerOAuth,
  sauvegarderMaintenant,
  synchroniserMaintenant,
  testerConnexionDrive,
  testerRestauration,
} from '../../lib/passerelle';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from '../cri/ui';

const ETAT_EXPORT = { succeeded: 'Terminé', pending: 'En attente', running: 'En cours', failed: 'En échec', configuration_required: 'Configuration requise', remplace: 'Remplacé' };
const dt = (v) => (v ? String(v).slice(0, 19).replace('T', ' ') : 'Jamais');
const Etat = ({ ok, libelle }) => (
  <span className={`font-data text-sm font-semibold ${ok ? FAVORABLE : DEFAVORABLE}`}>{libelle ?? (ok ? 'Vérifiée' : 'Non vérifiée')}</span>
);

const Etapes = ({ titre, etapes }) => (
  <div className="mt-3 border border-slate-200">
    <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
    <ul className="divide-y divide-slate-100">
      {etapes.map((e, i) => (
        <li key={`${e.etape}-${i}`} data-testid={`etape-${e.etape}`} className="flex items-start justify-between gap-3 px-3 py-1.5">
          <span className="text-sm text-slate-800">{e.etape}</span>
          <span className={`font-data text-xs ${e.ok ? FAVORABLE : DEFAVORABLE}`}>
            {e.ok ? 'OK' : 'ÉCHEC'}
            {e.detail ? ` · ${typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail)}`.slice(0, 220) : ''}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const ActivationOAuth = ({ auth, onMaj }) => {
  const [ids, setIds] = useState({ client_id: '', client_secret: '' });
  const [lien, setLien] = useState(null);
  const reconnexion = Boolean(auth.reconnexion_requise);
  const actif = auth.methode === 'oauth_utilisateur' && !reconnexion;

  const enregistrer = async () => {
    const { error } = await enregistrerIdentifiantsOAuth(ids.client_id, ids.client_secret);
    if (error) return toast.error(error);
    toast.success('Identifiants OAuth enregistrés côté serveur.');
    setIds({ client_id: '', client_secret: '' });
    onMaj();
  };

  const autoriser = async () => {
    const { error, data } = await obtenirUrlConsentement();
    if (error) return toast.error(error);
    setLien(data);
    window.open(data.url, '_blank', 'noopener');
  };

  return (
    <Carte
      testid="oauth-activation"
      titre="Écriture Drive — délégation OAuth utilisateur"
      sous="Un compte de service n’a pas de quota de stockage : pour déposer des fichiers dans un dossier de « Mon Drive », l’ERP doit agir au nom du compte propriétaire."
      action={
        actif ? (
          <button
            data-testid="oauth-revoquer"
            type="button"
            onClick={async () => {
              const { error } = await revoquerOAuth();
              if (error) return toast.error(error);
              toast.success('Délégation révoquée.');
              onMaj();
            }}
            className={btnLight}
          >
            Révoquer la délégation
          </button>
        ) : null
      }
    >
      {reconnexion ? (
        <div data-testid="oauth-reconnexion-requise" className="border border-red-200 bg-red-50 px-3 py-2.5">
          <p className={`text-sm font-semibold ${DEFAVORABLE}`}>Reconnexion Google requise</p>
          <p className="mt-1 text-xs text-slate-700">
            L’autorisation du compte {auth.compte_autorise ?? 'Google'} a expiré ou a été révoquée
            {auth.oauth_erreur_le ? ` (constaté le ${dt(auth.oauth_erreur_le)})` : ''}. Les synchronisations automatiques sont
            suspendues : la configuration du dossier, les fichiers déjà archivés, la file de traitement et les clés
            d’anti-doublon sont conservés. Après une nouvelle autorisation, les tâches en attente repartent seules.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button data-testid="oauth-reconnecter" type="button" onClick={autoriser} className={btn}>
              Reconnecter Google Drive
            </button>
            <span className="text-[11px] text-slate-500">
              URI de retour à autoriser dans Google Cloud :{' '}
              <code className="break-all">{`${window.location.origin}/api/passerelle/drive/oauth/retour`}</code>
            </span>
          </div>
          {lien?.url && (
            <p className="mt-2 break-all text-[11px] text-slate-500">
              Si l’onglet ne s’est pas ouvert, utilisez ce lien :{' '}
              <a href={lien.url} target="_blank" rel="noreferrer" className="underline">
                {lien.url.slice(0, 120)}…
              </a>
            </p>
          )}
        </div>
      ) : actif ? (
        <p data-testid="oauth-actif" className={`text-sm font-semibold ${FAVORABLE}`}>
          Délégation active pour {auth.identite?.compte ?? 'le compte autorisé'} — l’ERP peut téléverser dans le dossier cible.
        </p>
      ) : (
        <>
          <ol className="mb-3 list-decimal space-y-1 pl-5 text-xs text-slate-600">
            <li>
              Console Google Cloud (projet <code>our-lamp-508816-j0</code>) → <strong>API et services</strong> →{' '}
              <strong>Identifiants</strong> → <strong>Créer des identifiants</strong> → <strong>ID client OAuth</strong> → type{' '}
              <strong>Application Web</strong>.
            </li>
            <li>
              Dans « URI de redirection autorisés », ajoutez exactement :{' '}
              <code className="break-all">{`${window.location.origin}/api/passerelle/drive/oauth/retour`}</code>
            </li>
            <li>
              Écran de consentement : type <strong>Externe</strong>, et ajoutez <code>huilerie.he@gmail.com</code> comme{' '}
              <strong>utilisateur de test</strong> (sinon Google bloque l’autorisation).
            </li>
            <li>Collez ci-dessous l’identifiant et le secret client, enregistrez, puis autorisez en un clic.</li>
          </ol>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block sm:col-span-1">
              <span className={labelCls}>Identifiant client OAuth</span>
              <input
                data-testid="oauth-client-id"
                value={ids.client_id}
                onChange={(e) => setIds({ ...ids, client_id: e.target.value })}
                placeholder="…apps.googleusercontent.com"
                className={inputCls}
              />
            </label>
            <label className="block sm:col-span-1">
              <span className={labelCls}>Secret client</span>
              <input
                data-testid="oauth-client-secret"
                type="password"
                value={ids.client_secret}
                onChange={(e) => setIds({ ...ids, client_secret: e.target.value })}
                placeholder="GOCSPX-…"
                className={inputCls}
              />
            </label>
            <div className="flex items-end gap-2">
              <button data-testid="oauth-enregistrer" type="button" onClick={enregistrer} className={btn}>
                Enregistrer
              </button>
              <button data-testid="oauth-autoriser" type="button" onClick={autoriser} className={btnLight}>
                Autoriser Google
              </button>
            </div>
          </div>
          {lien?.url && (
            <p className="mt-2 break-all text-[11px] text-slate-500">
              Si l’onglet ne s’est pas ouvert, utilisez ce lien :{' '}
              <a href={lien.url} target="_blank" rel="noreferrer" className="underline">
                {lien.url.slice(0, 120)}…
              </a>
            </p>
          )}
        </>
      )}
    </Carte>
  );
};

export const SupervisionPasserelle = () => {  const [sup, setSup] = useState(null);
  const [journal, setJournal] = useState([]);
  const [exports, setExports] = useState([]);
  const [sauvegardes, setSauvegardes] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  const [recette, setRecette] = useState(null);
  const [manifeste, setManifeste] = useState(null);
  const [occupe, setOccupe] = useState('');

  const charger = useCallback(async () => {
    const [s, j, e, b] = await Promise.all([
      fetchSupervision(),
      fetchJournalSync(),
      fetchExportsMiroir(),
      fetchSauvegardes(),
    ]);
    setSup(s);
    setJournal(j);
    setExports(e);
    setSauvegardes(b);
    return [
      Array.isArray(j) ? j.length : 0,
      Array.isArray(e) ? e.length : 0,
      Array.isArray(b) ? b.length : 0,
    ];
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);
  useSondage(charger, 20000);

  const lancer = async (nom, fn, message) => {
    setOccupe(nom);
    const { error, data } = await fn();
    setOccupe('');
    if (error) return toast.error(error);
    toast.success(message);
    charger();
    return data;
  };

  const auth = sup?.authentification ?? {};
  const taches = sup?.taches ?? {};

  return (
    <div className="space-y-4">
      <Carte
        testid="supervision-drive"
        titre="Supervision Drive, miroir et sauvegardes"
        sous="Un accès en lecture ne vaut pas sauvegarde opérationnelle : lecture et écriture sont vérifiées séparément."
        action={
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="sup-tester-connexion"
              type="button"
              disabled={occupe === 'diag'}
              onClick={async () => {
                const d = await lancer('diag', testerConnexionDrive, 'Diagnostic de connexion terminé.');
                if (d) setDiagnostic(d);
              }}
              className={btnLight}
            >
              <ShieldAlert size={13} /> Tester la connexion
            </button>
            <button
              data-testid="sup-recette"
              type="button"
              disabled={occupe === 'recette'}
              onClick={async () => {
                const d = await lancer('recette', recetteDrive, 'Recette d’écriture exécutée.');
                if (d) setRecette(d);
              }}
              className={btnLight}
            >
              <CheckCircle size={13} /> Recette écriture
            </button>
            <button data-testid="sup-synchroniser" type="button" onClick={() => lancer('sync', synchroniserMaintenant, 'Synchronisation mise en file.')} className={btnLight}>
              <RefreshCw size={13} /> Synchroniser maintenant
            </button>
            <button data-testid="sup-sauvegarder" type="button" onClick={() => lancer('bk', sauvegarderMaintenant, 'Sauvegarde mise en file.')} className={btnLight}>
              <Database size={13} /> Sauvegarder maintenant
            </button>
            <button data-testid="sup-miroir" type="button" onClick={() => lancer('mir', exporterMiroir, 'Export miroir mis en file.')} className={btnLight}>
              <CloudUpload size={13} /> Exporter les pièces
            </button>
            <button data-testid="sup-journal-export" type="button" onClick={() => lancer('jr', exporterJournal, 'Export du journal mis en file.')} className={btnLight}>
              <FileText size={13} /> Exporter le journal
            </button>
            <button data-testid="sup-relancer" type="button" onClick={() => lancer('rl', relancerEchecs, 'Tâches en échec relancées.')} className={btnLight}>
              <Play size={13} /> Relancer les échecs
            </button>
          </div>
        }
      >
        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Méthode d’authentification', auth.methode === 'compte_service' ? 'Compte de service' : auth.methode === 'oauth_utilisateur' ? 'OAuth utilisateur' : 'Non configurée'],
            ['Identité utilisée', auth.identite?.compte ?? '—'],
            ['Dossier cible', auth.dossier_cible ?? '—'],
            ['Nature du dossier', auth.type_dossier === 'drive_partage' ? 'Drive partagé' : auth.type_dossier === 'mon_drive_partage' ? 'Dossier de Mon Drive (partagé)' : '—'],
          ].map(([k, v]) => (
            <div key={k} className="bg-white px-3 py-2.5">
              <p className="text-[11px] text-slate-500">{k}</p>
              <p className="break-all font-data text-sm font-semibold text-slate-900">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-3 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">État de lecture</p>
            <Etat ok={sup?.lecture?.verifiee} />
            <p className={`text-[11px] ${NEUTRE_DOUX}`}>{dt(sup?.lecture?.le)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">État d’écriture</p>
            <Etat ok={sup?.ecriture?.verifiee} />
            <p className={`text-[11px] ${NEUTRE_DOUX}`}>{dt(sup?.ecriture?.le)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Dernier import réussi</p>
            <p className="font-data text-sm font-semibold text-slate-900">{dt(sup?.lecture?.dernier_import_reussi)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Dernier export réussi</p>
            <p className="font-data text-sm font-semibold text-slate-900">{dt(sup?.ecriture?.dernier_export_reussi)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Dernière sauvegarde vérifiée</p>
            <p className="font-data text-sm font-semibold text-slate-900">{sup?.sauvegardes?.derniere_verifiee ?? 'Aucune'}</p>
            <p className={`text-[11px] ${NEUTRE_DOUX}`}>{dt(sup?.sauvegardes?.derniere_verifiee_le)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Dernier test de restauration</p>
            <p className="font-data text-sm font-semibold text-slate-900">
              {sup?.sauvegardes?.dernier_test_restauration ? (sup.sauvegardes.dernier_test_restauration.reussi ? 'Réussi' : 'En échec') : 'Jamais'}
            </p>
            <p className={`text-[11px] ${NEUTRE_DOUX}`}>{dt(sup?.sauvegardes?.dernier_test_restauration?.le)}</p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Sauvegarde planifiée</p>
            <p className="font-data text-sm font-semibold text-slate-900">
              {sup?.sauvegardes?.heure_planifiee ?? '02:00'} · {sup?.sauvegardes?.fuseau ?? 'Africa/Tunis'}
            </p>
          </div>
          <div className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">Tâches (attente / relance / échec)</p>
            <p className="font-data text-sm font-semibold text-slate-900">
              {taches.pending ?? 0} / {taches.retry_wait ?? 0} /{' '}
              <span className={(taches.failed ?? 0) + (taches.configuration_required ?? 0) > 0 ? DEFAVORABLE : ''}>
                {(taches.failed ?? 0) + (taches.configuration_required ?? 0)}
              </span>
            </p>
          </div>
        </div>

        {auth.reconnexion_requise && (
          <p data-testid="sup-reconnexion-requise" className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <strong>Reconnexion Google requise :</strong> l’autorisation a expiré ou a été révoquée. Les tâches Drive
            automatiques sont suspendues (aucune nouvelle tentative inutile) jusqu’à une nouvelle autorisation — bouton
            « Reconnecter Google Drive » ci-dessous.
          </p>
        )}
        {auth.conseil && (
          <p data-testid="sup-conseil" className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <strong>Correction attendue :</strong> {auth.conseil}
          </p>
        )}
        {auth.methode === 'absent' && (
          <p data-testid="sup-config-requise" className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Connexion Drive <strong>non configurée</strong> (état <code>configuration_required</code>) : renseignez
            <code> GOOGLE_SERVICE_ACCOUNT_JSON</code> (compte de service) ou le trio <code>GOOGLE_OAUTH_*</code> côté serveur.
            Le dépôt web et la photo mobile fonctionnent déjà sans Drive.
          </p>
        )}
        {diagnostic?.etapes && <Etapes titre="Diagnostic de connexion (étape par étape)" etapes={diagnostic.etapes} />}
        {recette?.etapes && <Etapes titre="Recette d’écriture réelle (création, téléversement, relecture)" etapes={recette.etapes} />}
      </Carte>

      <ActivationOAuth auth={auth} onMaj={charger} />

      <Carte testid="supervision-sauvegardes" titre="Sauvegardes versionnées" sous="Chaque sauvegarde conserve son manifeste, ses empreintes et ses dépendances documentaires.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Identifiant</th>
                <th className={th}>Début</th>
                <th className={th}>Objets</th>
                <th className={th}>Octets</th>
                <th className={th}>Intégrité</th>
                <th className={th}>État</th>
                <th className={th}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sauvegardes.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={7}>
                    Aucune sauvegarde enregistrée.
                  </td>
                </tr>
              )}
              {sauvegardes.map((b) => (
                <tr key={b.backup_id} data-testid={`sauvegarde-${b.backup_id}`}>
                  <td className={td}>{b.backup_id}</td>
                  <td className={td}>{dt(b.debut)}</td>
                  <td className={td}>{b.manifeste?.objets_total ?? '—'}</td>
                  <td className={td}>{fmt((b.octets ?? 0) / 1024, 1)} Ko</td>
                  <td className={`${td} ${b.integrite_verifiee ? FAVORABLE : NEUTRE_DOUX}`}>{b.integrite_verifiee ? 'Vérifiée' : 'Non vérifiée'}</td>
                  <td className={td}>{b.etat}</td>
                  <td className={td}>
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`manifeste-${b.backup_id}`}
                        type="button"
                        onClick={async () => setManifeste(await fetchManifeste(b.backup_id))}
                        className={btnLight}
                      >
                        Manifeste
                      </button>
                      <button
                        data-testid={`restaurer-test-${b.backup_id}`}
                        type="button"
                        onClick={() => lancer('rest', () => testerRestauration(b.backup_id), 'Test de restauration terminé.')}
                        className={btnLight}
                      >
                        Tester la restauration
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {manifeste && (
          <pre data-testid="manifeste-detail" className="mt-3 max-h-80 overflow-auto border border-slate-200 bg-slate-50 p-3 font-data text-[11px] text-slate-700">
            {JSON.stringify(manifeste, null, 2)}
          </pre>
        )}
      </Carte>

      <Carte testid="supervision-exports" titre="Exports miroir" sous="Identité unique par objet et version : une relance ne crée jamais une seconde copie.">
        <div className="max-h-80 overflow-auto border border-slate-200">
          <table className="w-full min-w-[900px] border-collapse">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className={th}>Type</th>
                <th className={th}>Objet</th>
                <th className={th}>Destination</th>
                <th className={th}>État</th>
                <th className={th}>Intégrité</th>
                <th className={th}>Tentatives</th>
                <th className={th}>Identifiant Drive</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exports.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={7}>
                    Aucun export miroir.
                  </td>
                </tr>
              )}
              {exports.map((e) => (
                <tr key={e.export_id} data-testid={`export-${e.export_id}`}>
                  <td className={td}>{e.type_export}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{String(e.objet_source).slice(0, 40)}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{e.destination}</td>
                  <td className={`${td} ${e.etat === 'succeeded' ? FAVORABLE : e.etat === 'configuration_required' || e.etat === 'failed' ? DEFAVORABLE : NEUTRE_DOUX}`}>
                    {ETAT_EXPORT[e.etat] ?? e.etat}
                  </td>
                  <td className={td}>{e.integrite_verifiee ? 'Vérifiée' : '—'}</td>
                  <td className={td}>{e.tentatives ?? 0}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{e.drive_file_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>

      <Carte testid="supervision-journal" titre="Journal de synchronisation, d’import et d’export" sous="Objet, corrélation, début, durée, résultat, catégorie d’erreur, tentatives et prochaine relance.">
        <div className="max-h-96 overflow-auto border border-slate-200">
          <table className="w-full min-w-[980px] border-collapse">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className={th}>Date</th>
                <th className={th}>Sens</th>
                <th className={th}>Étape</th>
                <th className={th}>Objet</th>
                <th className={th}>Résultat</th>
                <th className={th}>Durée</th>
                <th className={th}>Tentatives</th>
                <th className={th}>Erreur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {journal.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={8}>
                    Aucune tentative enregistrée.
                  </td>
                </tr>
              )}
              {journal.map((e) => (
                <tr key={e._id} data-testid={`sync-${e._id}`}>
                  <td className={td}>{dt(e.le)}</td>
                  <td className={td}>{e.sens === 'export' ? 'Export' : 'Import'}</td>
                  <td className={td}>{e.etape}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{String(e.objet ?? '').slice(0, 36) || '—'}</td>
                  <td className={`${td} ${e.etat === 'succeeded' ? FAVORABLE : DEFAVORABLE}`}>{ETATS_TACHE_LIBELLE[e.etat] ?? e.etat}</td>
                  <td className={td}>{e.duree_ms != null ? `${e.duree_ms} ms` : '—'}</td>
                  <td className={td}>{e.tentatives ?? 1}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>
                    {e.categorie_erreur ? `${e.categorie_erreur} · ` : ''}
                    {String(e.erreur ?? '').slice(0, 90) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
    </div>
  );
};
